import React from 'react';
import {
    WeezHeader,
    FilterSetController,
    Filter,
    ButtonDropdown,
    Button,
    Table,
    DropdownItem,
    Dropdown,
    TableCellDropdown,
    ContentCard,
    Card,
    Status,
    Icon,
    Tooltip
} from '@weezevent/nacre';
import LoadingTable from '../../../../components/loadingTable';
import { Inventory, Event } from '../../../../models';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import useDateFormat from '../../../../components/utils/useDateFormat';
import { InfoTextTooltip } from '../../../attendee/detail/components/infotext-tooltip/infoTextTooltip';
import DeleteModal from './DeleteModal';
import { SportsClubsWarningModal, SportsClubExportButton } from './sports-club-export/SportsClubsExport';
import css from '../../chart-list.module.css';

import useAppContext from '../../../../context/useAppContext';

export default function Inventories({ organization, chartId }) {
    const { t } = useTranslation();
    const history = useHistory();

    const defaultFilter = { search: '', active: 'true', has_active_bookings: 'true' };
    const [filters, setFilters] = React.useState(defaultFilter);

    const [inventories, loadingInventories, sync] = Inventory.useApiModel(
        {
            organizationId: organization.id,
            chartId,
            cache: false,
            query: filters
        },
        [organization, chartId, filters]
    );

    const [events, loadingEvents] = Event.useApiModel(
        {
            organizationId: organization.id,
            cache: false
        },
        [organization]
    );

    const handleSearch = React.useCallback((synth, value) => {
        setFilters(currentState => {
            const query = { ...currentState };
            query['search'] = value?.toLowerCase();
            return query;
        });
    }, []);

    const handleFilters = React.useCallback(value => {
        setFilters(currentState => {
            let query = { ...value, search: '' };

            if (currentState['search'] !== undefined) {
                query['search'] = currentState['search'];
            }
            return query;
        });
    }, []);

    const handleBack = React.useCallback(() => {
        history.push(`/ticket/O${organization.id}/charts/${chartId}/dashboard`);
    }, [history, organization, chartId]);

    const handleNew = React.useCallback(
        value => {
            history.push({
                pathname: `/ticket/O${organization.id}/charts/${chartId}/inventories/new`,
                state: { type: value }
            });
        },
        [history, organization, chartId]
    );

    return (
        <>
            <WeezHeader
                backLink={{
                    onClick: () => handleBack()
                }}
                rightComponent={
                    <ButtonDropdown
                        key="item-1"
                        items={
                            <>
                                <DropdownItem item={t('ticket.charts.chart.add_real_inventory')} onClick={() => handleNew('real')} />
                                <DropdownItem item={t('ticket.charts.chart.add_virtual_inventory')} onClick={() => handleNew('virtual')} />
                            </>
                        }
                        trigger={<Button primary icon={<Icon name={'plus'} margin={false} />} label={t('ticket.common.action.add')} />}
                    />
                }
                title={t('ticket.charts.chart.inventories')}
            />
            <Card>
                <ContentCard>
                    <FilterSetController
                        total={loadingInventories ? 0 : inventories.length}
                        labelTotal={t('ticket.charts.chartlist.inventory', {
                            count: inventories?.length || 0
                        })}
                        onChange={value => handleFilters(value)}
                        initialValues={filters}
                        defaultValues={defaultFilter}
                        onReset={() => setFilters(defaultFilter)}
                        search={{
                            onChange: (synth, value) => {
                                handleSearch(synth, value);
                            },
                            handleReset: () => {
                                handleSearch('');
                            },
                            placeholder: t('ticket.charts.chart.searchInventory'),
                            value: filters['search']
                        }}
                    >
                        <Filter
                            index="1"
                            labelCTA={t('ticket.common.action.apply')}
                            labelSelectAll={t('ticket.common.action.select-all')}
                            labelName={t('ticket.common.status')}
                            name="active"
                            options={[
                                {
                                    key: 'active',
                                    name: 'true',
                                    text: t('ticket.charts.inventory.active_inventory')
                                },
                                {
                                    key: 'inactive',
                                    name: 'false',
                                    text: t('ticket.charts.inventory.inactive_inventory')
                                }
                            ]}
                        />
                        <Filter
                            index="2"
                            labelCTA={t('ticket.common.action.apply')}
                            labelSelectAll={t('ticket.common.action.select-all')}
                            labelName={t('ticket.common.type')}
                            name="virtual"
                            options={[
                                {
                                    key: 'real',
                                    name: 'false',
                                    text: t('ticket.charts.inventory.real_inventory')
                                },
                                {
                                    key: 'virtual',
                                    name: 'true',
                                    text: t('ticket.charts.inventory.virtual_inventory')
                                }
                            ]}
                        />
                    </FilterSetController>
                </ContentCard>

                <Table useNew className={css['list__table']}>
                    <Table.Header>
                        <Table.Tr className={css['tr-header']}>
                            <th className={`${css['width-x-large']}`}>{t('ticket.common.inventory')}</th>
                            <th className={`${css['width-small']}`}>{t('ticket.common.date.start')}</th>
                            <th className={`${css['width-small']}`}>{t('ticket.common.date.end')}</th>
                            <th className={`${css['width-small']}`}>{t('ticket.common.type')}</th>
                            <th className={`${css['width-small']}`}>{t('ticket.common.status')}</th>
                            <th className={`${css['width-x-mini']}`}>{t('ticket.manager.tableHeader.actions')}</th>
                        </Table.Tr>
                    </Table.Header>
                    <Table.TBody>
                        {inventories?.map(inventory => (
                            <InventoryRow
                                key={inventory.uuid}
                                event={events?.find(ev => ev.id === inventory.event)}
                                chartId={chartId}
                                organization={organization}
                                inventory={inventory}
                                inventories={inventories}
                                sync={sync}
                            />
                        ))}
                    </Table.TBody>
                </Table>
                <LoadingTable loading={loadingInventories && loadingEvents} />
                {(!loadingInventories || !loadingEvents) && !inventories?.length && (
                    <Card.EmptyState
                        className={css['no-inventories']}
                        title={t('ticket.rates.infos.noInventories')}
                        subtitle={t('ticket.rates.infos.noInventories_subtitle')}
                        icon={<Icon name="list" />}
                        labelButton={t('ticket.charts.chart.addInventory')}
                        onClick={() => handleNew('real')}
                    />
                )}
            </Card>
        </>
    );
}

export const InventoryRow = React.memo(({ sync, chartId, event, inventory, inventories, organization }) => {
    const [{ chart }] = useAppContext();
    const history = useHistory();
    const { t } = useTranslation();
    const [modal, setModal] = React.useState(false);
    const [sportsClubWarningModal, setSportsClubWarningModal] = React.useState(false);

    const handleEvent = React.useCallback(() => {
        window.open(`/ticket/O${organization.id}/E${event.id}`, '_blank');
    }, [organization, event]);

    const editInventory = React.useCallback(() => {
        history.push({
            pathname: `/ticket/O${organization.id}/charts/${chartId}/inventories/${inventory.uuid}`,
            state: { type: inventory.virtual ? 'virtual' : 'real' }
        });
    }, [history, organization, chartId, inventory]);

    const inventoryTime = React.useCallback((item, value) => {
        const [, properTime, properDate] = useDateFormat(
            {
                date: item,
                event,
                dateStyle: 'short',
                timeStyle: 'short'
            },
            [event]
        );

        if (value === 'date') {
            return properDate;
        } else if (value === 'time') {
            return properTime;
        }
    });

    const inventoryType = React.useMemo(() => {
        return t(`ticket.partners.batch.inventories.${inventory.virtual ? 'virtual' : 'real'}`);
    }, [inventory]);

    const inventoryStatus = React.useMemo(() => {
        return (
            <Status color={inventory.active ? 'green' : 'orange'} responsive text={inventory.active ? t('ticket.manager.status.active') : t('ticket.manager.status.inactive')} />
        );
    });

    const inventoryTooltip = React.useMemo(() => {
        if (!inventory.active) {
            return t('ticket.charts.inventory.active_tooltip');
        }
        return t('ticket.charts.inventory.inactive_tooltip');
    }, [inventory]);

    const displaySportsClubExportButton = React.useMemo(() => {
        return chart.type !== 'default' && !inventory.virtual;
    }, [chart, inventory]);

    const displaySportsClubWarningModal = React.useMemo(() => {
        const { metadata } = inventory;
        const { match, season, visiting_club } = metadata;
        return !(match || season || visiting_club);
    }, [inventory]);

    return (
        <>
            <DeleteModal inventories={inventories} inventory={inventory} chartId={chartId} organization={organization} open={modal} onClose={setModal} sync={sync} />
            <SportsClubsWarningModal
                organization={organization}
                chart={chart}
                inventory={inventory}
                display={displaySportsClubWarningModal}
                open={sportsClubWarningModal}
                onClose={() => setSportsClubWarningModal(false)}
            />
            <Table.Tr className={`${css['tr-body']}`} onClick={editInventory}>
                <Table.Td className={`${css['width-x-large']}`}>
                    <div className={css['inventories-items']}>
                        <InfoTextTooltip title={inventory.name} />
                        <span className={css['inventories-subitems']}>
                            {event?.name}
                            <Tooltip triggerOnHover position={'top-start'} title={t('ticket.charts.chart.inventories_event_link')} margin={false}>
                                <Icon className={css['inventories-event-link']} name="external-link" onClick={handleEvent} />
                            </Tooltip>
                        </span>
                    </div>
                </Table.Td>
                <Table.Td className={`${css['width-small']}`}>
                    <div className={css['inventories-items']}>
                        <InfoTextTooltip
                            title={inventoryTime(inventory.start_date || event?.start_date, 'date')}
                            subtitle={inventoryTime(inventory.start_date || event?.start_date, 'time')}
                        />
                    </div>
                </Table.Td>
                <Table.Td className={`${css['width-small']}`}>
                    <div className={css['inventories-items']}>
                        <InfoTextTooltip
                            title={inventory.end_date ? inventoryTime(inventory.end_date, 'date') : ''}
                            subtitle={inventory.end_date ? inventoryTime(inventory.end_date, 'time') : ''}
                        />
                    </div>
                </Table.Td>
                <Table.Td className={`${css['width-small']}`}>
                    <InfoTextTooltip title={inventoryType} />
                </Table.Td>
                <Table.Td className={`${css['width-small']}`}>
                    <Tooltip triggerOnHover position={'top-start'} title={inventoryTooltip} margin={false}>
                        <span>{inventoryStatus}</span>
                    </Tooltip>
                </Table.Td>
                <TableCellDropdown renderWhenClosed className={`${css['width-x-mini']} ${css['inventories-actions']}`}>
                    <Dropdown.Item item={t('ticket.common.action.edit')} onClick={editInventory} />
                    {displaySportsClubExportButton && (
                        <>
                            <Dropdown.Line />
                            <SportsClubExportButton
                                organization={organization}
                                chart={chart}
                                inventory={inventory}
                                display={!displaySportsClubWarningModal}
                                onClick={() => setSportsClubWarningModal(true)}
                            />
                        </>
                    )}
                    <Dropdown.Line />
                    <Dropdown.Item item={t('ticket.common.action.delete')} deleteItem onClick={() => setModal(true)} />
                </TableCellDropdown>
            </Table.Tr>
        </>
    );
});
